import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";

import { useBankController } from "./use-bank-controller";

type BankControllerContextValue = ReturnType<typeof useBankController> & {
  isStatementsSlideoutOpen: boolean;
  isConnectionsSlideoutOpen: boolean;
  openStatementsSlideout: () => void;
  closeStatementsSlideout: () => void;
  openConnectionsSlideout: () => void;
  closeConnectionsSlideout: () => void;
};

const BankControllerContext = createContext<BankControllerContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export const useBankControllerContext = (): BankControllerContextValue => {
  const context = useContext(BankControllerContext);
  if (context === null) {
    throw new Error("useBankControllerContext must be used inside BankControllerProvider");
  }
  return context;
};

export const BankControllerProvider = ({ children }: { children: ReactNode }) => {
  const bankController = useBankController();
  const [isStatementsSlideoutOpen, setIsStatementsSlideoutOpen] = useState(false);
  const [isConnectionsSlideoutOpen, setIsConnectionsSlideoutOpen] = useState(false);

  useEffect(() => {
    if (bankController.shouldConfirmAccountSelection) {
      setIsConnectionsSlideoutOpen(true);
    }
  }, [bankController.shouldConfirmAccountSelection]);

  const value: BankControllerContextValue = {
    ...bankController,
    isStatementsSlideoutOpen,
    isConnectionsSlideoutOpen,
    openStatementsSlideout: () => setIsStatementsSlideoutOpen(true),
    closeStatementsSlideout: () => setIsStatementsSlideoutOpen(false),
    openConnectionsSlideout: () => setIsConnectionsSlideoutOpen(true),
    closeConnectionsSlideout: () => setIsConnectionsSlideoutOpen(false),
  };

  return <BankControllerContext.Provider value={value}>{children}</BankControllerContext.Provider>;
};
